import { Link } from "react-router-dom";
import { ArrowRight, Recycle, Leaf, Sprout, TreePine, Droplets, Wind, PackageCheck, Factory, Globe2 } from "lucide-react";
import { Reveal, MaskedLines } from "../components/store/Reveal";
import { Marquee } from "../components/store/Marquee";

const IMG_FRONT = "/packaging-2.jpg";
const IMG_OPEN = "/packaging-1.jpg";
const IMG_TRAY = "/packaging-3.jpg";

const STATS = [
  { v: "100%", l: "Riciclabile nella carta" },
  { v: "-80%", l: "Plastica rispetto alle vaschette tradizionali" },
  { v: "FSC®", l: "Carta da foreste gestite responsabilmente" },
  { v: "0", l: "Sbiancanti chimici: kraft naturale" },
];

const BENEFITS = [
  { icon: Recycle, t: "Riciclabile al 100%", d: "Scatola e vassoio in cellulosa si riciclano nella carta: un solo gesto, nessun dubbio davanti al bidone. Il ciclo della carta può ripetersi fino a 7 volte." },
  { icon: Leaf, t: "Compostabile e biodegradabile", d: "La fibra kraft non trattata si biodegrada in poche settimane, senza rilasciare microplastiche nel suolo o nei mari." },
  { icon: Sprout, t: "Meno plastica in circolo", d: "Il vassoio in polpa di cellulosa sostituisce le classiche vaschette in plastica nera, notoriamente difficili da riciclare: resta solo un sottilissimo film protettivo per uso alimentare." },
  { icon: TreePine, t: "Fibre da foreste certificate", d: "Il cartoncino proviene da filiere FSC®: per ogni albero utilizzato ne vengono ripiantati di nuovi, tutelando la biodiversità delle foreste." },
  { icon: Droplets, t: "Inchiostri a base vegetale", d: "Le grafiche sono stampate con inchiostri a base d'acqua e vegetale, privi di solventi aggressivi: più sicuri per l'ambiente e per il riciclo." },
  { icon: Wind, t: "Meno CO₂ nei trasporti", d: "Il kraft è leggero e le scatole viaggiano piatte fino al confezionamento: più prodotti per viaggio, meno camion su strada, meno emissioni." },
  { icon: Factory, t: "Produzione a basso impatto", d: "La lavorazione della fibra kraft naturale richiede meno acqua ed energia rispetto alla plastica vergine e non usa sbiancanti al cloro." },
  { icon: PackageCheck, t: "Dal freezer al forno", d: "Il vassoio in cellulosa resiste al gelo a -18°C, al microonde e al forno: nessun travaso, nessun piatto extra da lavare, meno sprechi d'acqua." },
  { icon: Globe2, t: "Economia circolare", d: "Ogni confezione riciclata torna materia prima: scegliere il kraft significa alimentare un ciclo virtuoso invece di una discarica." },
];

const STEPS = [
  { n: "01", t: "Proteggi", d: "La scatola kraft isola e protegge il piatto surgelato durante il viaggio refrigerato." },
  { n: "02", t: "Scalda", d: "Il vassoio in cellulosa va direttamente in microonde (4 min) o in forno a 180°C (15 min)." },
  { n: "03", t: "Ricicla", d: "Scatola e vassoio nella carta, il sottile film nella plastica: raccolta differenziata in 10 secondi." },
];

export default function Packaging() {
  return (
    <div data-testid="packaging-page">
      {/* HERO */}
      <section className="max-w-7xl mx-auto px-4 sm:px-8 pt-14 lg:pt-24 pb-16 lg:pb-24 grid lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-6">
          <p className="font-mono-accent text-xs uppercase tracking-[0.3em] text-[#2E8A57] mb-6">Packaging sostenibile</p>
          <h1 className="font-serif-display font-bold text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight text-[#1C1917]">
            <MaskedLines lines={["Buono per te,"]} />
            <MaskedLines lines={["buono per", "il pianeta."]} lineClassName="italic text-[#2E8A57]" startDelay={0.35} />
          </h1>
          <p className="mt-7 text-base md:text-lg text-[#44403C] max-w-lg leading-relaxed">
            Ogni piatto arriva in una confezione in carta kraft naturale con vassoio in cellulosa: un packaging pensato per proteggere il cibo — e l'ambiente. Riciclabile, compostabile e quasi senza plastica.
          </p>
          <div className="mt-9 flex flex-wrap gap-4">
            <Link to="/prodotti" data-testid="packaging-shop-btn" className="btn-tuscan inline-flex items-center gap-2 px-8 py-4 rounded-full font-semibold text-sm tracking-wide">
              Ordina ora <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
        <div className="lg:col-span-6">
          <Reveal>
            <div className="rounded-[2rem] overflow-hidden border border-[#E7E0D8] shadow-[0_40px_80px_-40px_rgba(28,25,23,0.4)]">
              <img src={IMG_FRONT} alt="Confezione kraft Trofie al Pesto Genovese" className="w-full h-full object-cover" />
            </div>
          </Reveal>
        </div>
      </section>

      {/* STATS */}
      <section className="bg-[#243329] text-[#FDFBF7]">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 py-14 grid grid-cols-2 lg:grid-cols-4 gap-10">
          {STATS.map((s, i) => (
            <Reveal key={s.v} delay={i * 0.08}>
              <p className="font-serif-display font-bold text-4xl lg:text-5xl text-[#7DC242]">{s.v}</p>
              <p className="mt-2 text-sm text-[#FDFBF7]/70 leading-snug">{s.l}</p>
            </Reveal>
          ))}
        </div>
      </section>

      {/* GALLERY */}
      <section className="max-w-7xl mx-auto px-4 sm:px-8 py-20 lg:py-28">
        <Reveal>
          <p className="font-mono-accent text-xs uppercase tracking-[0.3em] text-[#2E8A57] mb-3">La confezione</p>
          <h2 className="font-serif-display font-bold text-2xl sm:text-3xl lg:text-4xl text-[#1C1917] tracking-tight max-w-2xl">
            Carta kraft naturale, vassoio in cellulosa. Niente di superfluo.
          </h2>
        </Reveal>
        <div className="mt-12 grid md:grid-cols-3 gap-6">
          {[
            { img: IMG_FRONT, t: "La scatola", d: "Cartoncino kraft FSC® non sbiancato, stampato con inchiostri vegetali." },
            { img: IMG_OPEN, t: "All'apertura", d: "Istruzioni chiare sul coperchio: microonde 4 min o forno 15 min, dal freezer alla tavola." },
            { img: IMG_TRAY, t: "Il vassoio", d: "Polpa di cellulosa resistente a -18°C, microonde e forno. Si ricicla nella carta." },
          ].map((c, i) => (
            <Reveal key={c.t} delay={i * 0.1}>
              <div className="card-dish rounded-[2rem] overflow-hidden border border-[#E7E0D8] bg-white group">
                <div className="aspect-square overflow-hidden">
                  <img src={c.img} alt={c.t} className="dish-img w-full h-full object-cover" />
                </div>
                <div className="p-6">
                  <h3 className="font-serif-display font-bold text-xl text-[#1C1917] group-hover:text-[#2E8A57] transition-colors">{c.t}</h3>
                  <p className="mt-2 text-sm text-[#44403C] leading-relaxed">{c.d}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* BENEFITS */}
      <section className="bg-[#F5EFE5]">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 py-20 lg:py-28">
          <Reveal>
            <p className="font-mono-accent text-xs uppercase tracking-[0.3em] text-[#2E8A57] mb-3">Perché il kraft</p>
            <h2 className="font-serif-display font-bold text-2xl sm:text-3xl lg:text-4xl text-[#1C1917] tracking-tight max-w-2xl">
              Tutti i vantaggi per l'ambiente
            </h2>
          </Reveal>
          <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-px bg-[#E7E0D8] border border-[#E7E0D8] rounded-2xl overflow-hidden">
            {BENEFITS.map((b, i) => (
              <Reveal key={b.t} delay={(i % 3) * 0.08} className="bg-[#FDFBF7] p-8 group hover:bg-white transition-colors duration-300">
                <div className="w-11 h-11 rounded-full bg-[#2E8A57]/10 flex items-center justify-center mb-5">
                  <b.icon className="w-5 h-5 text-[#2E8A57]" />
                </div>
                <h3 className="font-serif-display font-bold text-xl text-[#1C1917] group-hover:text-[#2E8A57] transition-colors">{b.t}</h3>
                <p className="mt-2.5 text-sm text-[#44403C] leading-relaxed">{b.d}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* LIFECYCLE */}
      <section className="max-w-7xl mx-auto px-4 sm:px-8 py-20 lg:py-28">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <Reveal>
            <div className="rounded-[2rem] overflow-hidden border border-[#E7E0D8]">
              <img src={IMG_OPEN} alt="Scatola aperta con vassoio in cellulosa" className="w-full h-full object-cover" />
            </div>
          </Reveal>
          <div>
            <Reveal>
              <p className="font-mono-accent text-xs uppercase tracking-[0.3em] text-[#2E8A57] mb-3">Il ciclo di vita</p>
              <h2 className="font-serif-display font-bold text-2xl sm:text-3xl lg:text-4xl text-[#1C1917] tracking-tight">
                Tre gesti, zero sensi di colpa.
              </h2>
            </Reveal>
            <div className="mt-10 space-y-8">
              {STEPS.map((s, i) => (
                <Reveal key={s.n} delay={i * 0.1}>
                  <div className="flex gap-6">
                    <p className="font-mono-accent text-sm text-[#7DC242] pt-1">{s.n}</p>
                    <div>
                      <h3 className="font-serif-display font-bold text-xl text-[#1C1917]">{s.t}</h3>
                      <p className="mt-1.5 text-sm text-[#44403C] leading-relaxed">{s.d}</p>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-[#243329] text-[#FDFBF7]">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 py-20 text-center">
          <Reveal>
            <h2 className="font-serif-display font-bold text-3xl sm:text-4xl tracking-tight">Gusto italiano, coscienza pulita.</h2>
            <p className="mt-4 text-[#FDFBF7]/70 max-w-lg mx-auto leading-relaxed">
              Scegli piatti gluten free artigianali consegnati in un packaging che rispetta il pianeta.
            </p>
            <Link to="/prodotti" data-testid="packaging-cta-btn" className="mt-8 inline-flex items-center gap-2 px-8 py-4 rounded-full font-semibold text-sm bg-[#7DC242] text-[#243329] hover:bg-[#6BAB35] transition-colors">
              Scopri i piatti <ArrowRight className="w-4 h-4" />
            </Link>
          </Reveal>
        </div>
      </section>

      <Marquee />
    </div>
  );
}
